import { z } from 'zod';
import { FormSection, FormField, generateFormSchema, SchemaGenerationOptions } from './generalform';

export interface MachineData {
  general?: { [key: string]: any };
  [key: string]: any;
}

export const addCustomMachineForm: FormSection = {
  General: [
    {
      name: 'Name',
      db_name: 'mac_name',
      field: 'text',
      options: '',
      unit: '',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'OEM Name',
      db_name: 'oem_name',
      field: 'text',
      options: '',
      unit: '',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Technology',
      db_name: 'tech',
      field: 'select',
      options: [],
      unit: '',
      required: true,
      prc: '',
      component: 'SparkSelect',
      value: null,
    },
    {
      name: 'Process',
      db_name: 'prc_uid',
      field: 'select',
      options: [],
      unit: '',
      required: true,
      prc: '',
      component: 'SparkSelect',
      value: null,
    },
    {
      name: 'Machine Hourly Rate',
      db_name: 'mac_cost_ph',
      field: 'number',
      options: [0.1, 100000],
      unit: '€/h',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Build envelope x-direction',
      db_name: 'bld_size_x',
      field: 'number',
      options: [0.1, 100000],
      unit: 'mm',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Build envelope y-direction',
      db_name: 'bld_size_y',
      field: 'number',
      options: [0.1, 100000],
      unit: 'mm',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Build envelope z-direction',
      db_name: 'bld_size_z',
      field: 'number',
      options: [0.1, 100000],
      unit: 'mm',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Process time per layer',
      db_name: 'mac_prc_t_plyr',
      field: 'number',
      options: [0, 100000],
      unit: 's',
      required: false,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Fixed machine cost per job',
      db_name: 'fix_mac_cost_pj',
      field: 'number',
      options: [0, 1000000],
      unit: '€',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Setup time',
      db_name: 'setup_time',
      field: 'number',
      options: [0, 1000000],
      unit: 'h',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Unloading time',
      db_name: 'unloading_time',
      field: 'number',
      options: [0, 1000000],
      unit: 'h',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Machine idle time',
      db_name: 'mac_idl_t_pj',
      field: 'number',
      options: [0, 1000000],
      unit: 'h',
      required: true,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Staff supervision time',
      db_name: 'staff_time_factor',
      field: 'number',
      options: [0, 100],
      unit: '%',
      required: false,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Access',
      db_name: 'restriction',
      field: 'select',
      options: [],
      unit: '',
      required: false,
      prc: '',
      component: 'SparkSelect',
      value: null,
    },
    {
      name: 'Average Power Consumption',
      db_name: 'power_consumption',
      field: 'number',
      options: [0, 10000],
      unit: 'kW',
      required: false,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
    {
      name: 'Id',
      db_name: 'mac_id',
      field: 'text',
      options: '',
      unit: '',
      required: false,
      prc: '',
      component: 'SparkInput',
      value: null,
    },
  ],
};

export const populateMachineFormValues = (machineData: MachineData): FormSection => {
  const tempForm: FormSection = JSON.parse(JSON.stringify(addCustomMachineForm));

  function getValueFromMachineData(machineData: MachineData, db_name: string): any {
    const keys = db_name.split('.');
    let value: any = machineData;
    for (const key of keys) {
      value = value ? value[key] : null;
      if (value === null || value === undefined) break;
    }
    return value;
  }

  function assignValues(formArray: FormField[], data: MachineData): void {
    formArray.forEach(formField => {
      if (formField.db_name) {
        let value: any;
        if (data.general && data.general[formField.db_name] !== undefined) {
          value = data.general[formField.db_name];
        } else {
          value = getValueFromMachineData(data, formField.db_name);
        }

        if (value !== undefined && value !== null) {
          formField.value = value;
        }
      }
    });
  }

  for (const section in tempForm) {
    if (Array.isArray(tempForm[section])) {
      assignValues(tempForm[section], machineData);
    }
  }

  return tempForm;
};

/**
 * Generates a Zod schema from the machine form definition
 * @param machineFormData - The machine form definition object
 * @param selectedTech - The selected technology for tech-specific validations
 * @returns The generated Zod schema
 */
export const generateMachineSchema = (
  machineFormData: FormSection,
  selectedTech: string | null = null
): z.ZodObject<any> => {
  // Machine-specific field overrides
  const fieldOverrides: { [fieldName: string]: z.ZodType<any> } = {
    // Name fields with specific max length
    mac_name: z.string().min(1, 'Required').max(250, 'Machine name cannot exceed 250 characters'),
    oem_name: z.string().min(1, 'Required').max(250, 'OEM name cannot exceed 250 characters'),
    tech: z.string().min(1, 'Required'),
    prc_uid: z.string().min(1, 'Required'),

    // Required number fields with string-to-number transformation
    mac_cost_ph: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0, 'Required, cannot be less than 0').max(100000, 'Cannot be more than 100000')),
    bld_size_x: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0.1, 'Required, cannot be less than 0.1').max(100000, 'Cannot be more than 100000')),
    bld_size_y: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0.1, 'Required, cannot be less than 0.1').max(100000, 'Cannot be more than 100000')),
    bld_size_z: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0.1, 'Required, cannot be less than 0.1').max(100000, 'Cannot be more than 100000')),
    fix_mac_cost_pj: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0, 'Required, cannot be less than 0').max(1000000, 'Cannot be more than 1000000')),
    setup_time: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0, 'Required, cannot be less than 0').max(1000000, 'Cannot be more than 1000000')),
    unloading_time: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0, 'Required, cannot be less than 0').max(1000000, 'Cannot be more than 1000000')),
    mac_idl_t_pj: z
      .union([z.string().trim(), z.number()])
      .transform(val => (val === '' ? undefined : Number(val)))
      .pipe(z.number().min(0, 'Required, cannot be less than 0').max(1000000, 'Cannot be more than 1000000')),

    // Optional fields with special preprocessing
    mac_prc_t_plyr: z.preprocess(
      val => (val === '' || val === null || val === undefined ? undefined : Number(val)),
      z.number().min(0, 'Cannot be less than 0').max(100000, 'Cannot be more than 100000').optional()
    ),
    staff_time_factor: z.preprocess(
      val => (val === '' || val === null || val === undefined ? undefined : Number(val)),
      z.number().min(0, 'Cannot be less than 0').max(100, 'Cannot be more than 100').optional()
    ),
    power_consumption: z.preprocess(
      val => (val === '' || val === null || val === undefined ? undefined : Number(val)),
      z.number().min(0, 'Cannot be less than 0').max(10000, 'Cannot be more than 10000').optional().nullable()
    ),
    restriction: z.string().max(50, 'Maximal length is 50').optional().nullable(),
  };

  const options: SchemaGenerationOptions = {
    selectedTech,
    fieldOverrides,
    // Exclude technology-specific sections since machines don't have these
    excludeSections: ['Additive Manufacturing', 'Casting', 'Milling', 'Fire and Smoke'],
  };

  return generateFormSchema(machineFormData, options);
};
